import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wrench, Droplets, BarChart3, Users, ClipboardList, CreditCard, Calendar, Settings, ShieldCheck, ArrowRight, Check, ChevronRight } from 'lucide-react';

const TALLER_SCREENSHOT = '/screenshots/taller-dashboard.jpg';
const CARWASH_SCREENSHOT = '/screenshots/carwash-dashboard.jpg';

const beneficiosTaller = [
  { icono: ClipboardList, titulo: 'Ordenes de Trabajo', desc: 'Gestiona reparaciones con piezas, costos y seguimiento de estado en tiempo real.' },
  { icono: Users, titulo: 'Clientes y Vehiculos', desc: 'Base de datos completa con historial de servicio por vehiculo.' },
  { icono: CreditCard, titulo: 'Pagos y Contabilidad', desc: 'Control de ingresos, gastos, nomina y resumen financiero.' },
  { icono: Calendar, titulo: 'Citas y Recordatorios', desc: 'Agenda citas y envia recordatorios a tus clientes.' },
  { icono: BarChart3, titulo: 'Dashboard Inteligente', desc: 'Visualiza el estado de tu negocio con graficas y metricas.' },
  { icono: Settings, titulo: 'PDF y WhatsApp', desc: 'Genera ordenes en PDF y notifica por WhatsApp automaticamente.' },
];

const beneficiosCarwash = [
  { icono: Droplets, titulo: 'Turnos en Tiempo Real', desc: 'Cola de vehiculos con estados: espera, lavando, listo, entregado.' },
  { icono: CreditCard, titulo: 'Punto de Venta', desc: 'Vende productos y servicios con control de caja integrado.' },
  { icono: BarChart3, titulo: 'Corte de Caja', desc: 'Cierra el dia con conciliacion de efectivo y reporte PDF.' },
  { icono: Users, titulo: 'Descuento Frecuente', desc: 'Descuentos automaticos para clientes que regresan.' },
  { icono: Calendar, titulo: 'Citas', desc: 'Sistema de citas que se convierte en turno automaticamente.' },
  { icono: Settings, titulo: 'Contabilidad Completa', desc: 'Ingresos, gastos, nomina y resumen financiero detallado.' },
];

export default function Landing() {
  const navigate = useNavigate();
  const [precios, setPrecios] = useState({ mxn: 399, usd: 15 });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/precios-publicos`)
      .then(r => r.json())
      .then(d => setPrecios(d))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="landing-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-sm bg-amber-500/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-amber-500" />
            </div>
            <span className="font-barlow text-xl font-bold text-amber-500 uppercase tracking-wider">Talleres PRO</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-400 hover:text-slate-200" data-testid="landing-login-btn">
              Iniciar Sesion
            </Button>
            <Button onClick={() => navigate('/registro')} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="landing-registro-btn">
              Prueba Gratis <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Badge className="mb-6 bg-amber-500/10 text-amber-400 border-amber-700 text-sm px-4 py-1">
            7 dias de prueba gratis - sin tarjeta
          </Badge>
          <h1 className="font-barlow text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-slate-50 leading-tight">
            Administra tu <span className="text-amber-500">taller</span> o <span className="text-cyan-400">car wash</span> como un profesional
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Software para gestion de talleres y carwash: ordenes de trabajo, clientes, agenda, pagos y control total del negocio en un solo lugar.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => navigate('/registro')} size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm text-base px-8 h-12 shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_50px_rgba(245,158,11,0.5)] transition-all"
              data-testid="hero-registro-btn">
              Comenzar Prueba Gratis <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" size="lg" onClick={() => document.getElementById('beneficios')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm text-base px-8 h-12">
              Ver Funciones <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-500" />
                <h3 className="font-barlow text-xl font-bold text-amber-500 uppercase">Talleres PRO</h3>
              </div>
              <div className="rounded-md border border-slate-700/50 overflow-hidden shadow-2xl shadow-amber-500/5">
                <img src={TALLER_SCREENSHOT} alt="Talleres PRO Dashboard" className="w-full" loading="lazy" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Droplets className="w-5 h-5 text-cyan-400" />
                <h3 className="font-barlow text-xl font-bold text-cyan-400 uppercase">WashPro</h3>
              </div>
              <div className="rounded-md border border-slate-700/50 overflow-hidden shadow-2xl shadow-cyan-500/5">
                <img src={CARWASH_SCREENSHOT} alt="WashPro Dashboard" className="w-full" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="beneficios" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Talleres PRO */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-3">
                <Wrench className="w-6 h-6 text-amber-500" />
                <h2 className="font-barlow text-2xl sm:text-3xl font-bold uppercase text-slate-50">Talleres PRO - Taller Mecanico</h2>
              </div>
              <p className="text-slate-400 text-base">Todo lo que necesitas para gestionar tu taller automotriz</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {beneficiosTaller.map((b, i) => {
                const Icon = b.icono;
                return (
                  <Card key={i} className="bg-slate-900/50 border-slate-800 hover:border-amber-700/50 transition-colors" data-testid={`beneficio-taller-${i}`}>
                    <CardContent className="p-5">
                      <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-amber-500" />
                      </div>
                      <h4 className="font-barlow font-bold text-slate-100 mb-1">{b.titulo}</h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* WashPro */}
          <div>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-3">
                <Droplets className="w-6 h-6 text-cyan-400" />
                <h2 className="font-barlow text-2xl sm:text-3xl font-bold uppercase text-slate-50">WashPro - Car Wash</h2>
              </div>
              <p className="text-slate-400 text-base">Gestiona tu autolavado con tecnologia de punta</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {beneficiosCarwash.map((b, i) => {
                const Icon = b.icono;
                return (
                  <Card key={i} className="bg-slate-900/50 border-slate-800 hover:border-cyan-700/50 transition-colors" data-testid={`beneficio-carwash-${i}`}>
                    <CardContent className="p-5">
                      <div className="w-10 h-10 rounded-sm bg-cyan-500/10 flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-cyan-400" />
                      </div>
                      <h4 className="font-barlow font-bold text-slate-100 mb-1">{b.titulo}</h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-slate-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-barlow text-2xl sm:text-3xl font-bold uppercase text-slate-50 mb-3">Simple y Transparente</h2>
          <p className="text-slate-400 mb-12 text-base">Empieza gratis, paga cuando estes listo</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card className="bg-slate-900/60 border-amber-700/50 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" />
              <CardContent className="p-6 text-left">
                <Badge className="mb-4 bg-amber-500/10 text-amber-400 border-amber-700">Recomendado</Badge>
                <h3 className="font-barlow text-xl font-bold text-slate-100">Prueba Gratis</h3>
                <p className="text-3xl font-bold text-amber-500 mt-2 font-barlow">$0 <span className="text-sm text-slate-500 font-normal">/ 7 dias</span></p>
                <ul className="mt-5 space-y-2">
                  {['Acceso completo a todas las funciones', 'Sin tarjeta de credito', 'Sin compromisos', 'Soporte incluido'].map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/registro')} className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="pricing-registro-btn">
                  Comenzar Ahora
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-700">
              <CardContent className="p-6 text-left">
                <Badge className="mb-4 bg-slate-500/10 text-slate-400 border-slate-700">Despues de la prueba</Badge>
                <h3 className="font-barlow text-xl font-bold text-slate-100">Plan Mensual</h3>
                <p className="text-3xl font-bold text-emerald-400 mt-2 font-barlow">${precios.mxn} <span className="text-sm text-slate-500 font-normal">MXN / mes</span></p>
                {precios.usd > 0 && (
                  <p className="text-lg font-bold text-indigo-400 mt-1 font-barlow">${precios.usd} <span className="text-sm text-slate-500 font-normal">USD / mes fuera de Mexico</span></p>
                )}
                <ul className="mt-5 space-y-2">
                  {['Todas las funciones incluidas', 'Paga con Mercado Pago, OXXO o SPEI', 'Cobro automatico disponible', 'Cancela cuando quieras'].map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" onClick={() => navigate('/registro')} className="w-full mt-6 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm">
                  Comenzar Prueba Gratis
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
            <h2 className="font-barlow text-xl font-bold uppercase text-slate-100">Tu informacion esta segura</h2>
          </div>
          <p className="text-slate-400 text-sm max-w-xl mx-auto mb-6">
            Tus datos estan protegidos y encriptados. No compartimos tu informacion con terceros.
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
            <button onClick={() => navigate('/privacidad')} className="hover:text-slate-300 underline underline-offset-2" data-testid="landing-privacidad-link">Aviso de Privacidad</button>
            <span>|</span>
            <button onClick={() => navigate('/terminos')} className="hover:text-slate-300 underline underline-offset-2" data-testid="landing-terminos-link">Terminos y Condiciones</button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-barlow text-2xl sm:text-3xl font-bold uppercase text-slate-50 mb-4">
            Listo para modernizar tu negocio?
          </h2>
          <p className="text-slate-400 mb-8 text-base">
            Unete a los talleres y car wash que ya usan Talleres PRO para crecer.
          </p>
          <Button onClick={() => navigate('/registro')} size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm text-base px-10 h-12 shadow-[0_0_30px_rgba(245,158,11,0.3)]"
            data-testid="final-cta-btn">
            Crear Mi Cuenta Gratis <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-amber-500" />
            <span className="font-barlow text-sm font-bold text-amber-500 uppercase">Talleres PRO</span>
          </div>
          <div className="flex gap-6 text-xs text-slate-500">
            <button onClick={() => navigate('/privacidad')} className="hover:text-slate-300">Aviso de Privacidad</button>
            <button onClick={() => navigate('/terminos')} className="hover:text-slate-300">Terminos y Condiciones</button>
          </div>
          <p className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Talleres PRO. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
